import {
  Box,
  Button,
  CloseButton,
  InputWrapper,
  Popover,
  Stack,
  TextInput,
  TextInputProps,
} from "@mantine/core"
import dayjs from "dayjs"
import { DatePicker, MonthPicker, YearPicker } from "@mantine/dates"

// StartOrEnd Prop is a tight coupling to our makros / custom date picker setup and should be removed.
export interface CustomDateInputProps {
  inputProps?: TextInputProps
  required?: boolean
  validate?: boolean
  placeholder?: string
  label?: string
  value?: Date | undefined
  onChange?: (value: Date | undefined) => void
  dropdownChildren?: React.ComponentType<CustomDateInputChildrenProps>[]
  startOrEnd?: "start" | "end"
  minDate?: Date
  maxDate?: Date
  clearable?: boolean
  variant?: "default" | "month" | "year"
}

export interface CustomDateInputChildrenProps {
  dateValue?: Date | undefined
  setDateValue?: React.Dispatch<Date | undefined>
  startOrEnd?: "start" | "end"
}

export function CustomDateInput({
  required,
  label,
  placeholder,
  validate,
  value,
  onChange,
  dropdownChildren,
  startOrEnd,
  minDate,
  maxDate,
  inputProps,
  clearable,
  variant,
}: CustomDateInputProps) {
  const [opened, setOpened] = React.useState(false)
  const [internalDateValue, setInternalDateValue] = React.useState<
    Date | undefined
  >(undefined)
  const [isValidDate, setIsValidDate] = React.useState<boolean>(true)

  const dateValue = value ?? internalDateValue

  const validateDate = (value: Date | undefined): boolean => {
    if (value && dayjs(value).isValid()) {
      if (
        (minDate && dayjs(value).isBefore(dayjs(minDate), "day")) ||
        (maxDate && dayjs(value).isAfter(dayjs(maxDate), "day"))
      ) {
        setIsValidDate(false)
        return false
      }
      setIsValidDate(true)
      return true
    }
    setIsValidDate(false)
    return false
  }

  const handleDateChange = (newValue: Date | undefined) => {
    if (newValue) {
      if (
        (minDate && dayjs(newValue).isBefore(dayjs(minDate), "day")) ||
        (maxDate && dayjs(newValue).isAfter(dayjs(maxDate), "day"))
      ) {
        setIsValidDate(false)
        return
      }
    }
    if (onChange) {
      onChange(newValue)
    } else {
      setInternalDateValue(newValue)
    }
    setIsValidDate(true)
    setOpened(false)
  }

  return (
    <Popover opened={opened} onChange={setOpened} withArrow closeOnEscape>
      <Popover.Target>
        {variant === "month" || variant === "year" ? (
          <InputWrapper flex="1" label={label}>
            <Box>
              <Button
                onClick={() => setOpened((previousValue) => !previousValue)}
                variant="subtle"
              >
                {dateValue
                  ? dayjs(dateValue).format(
                      variant === "month" ? "MMMM YYYY" : "YYYY"
                    )
                  : ""}
              </Button>
            </Box>
          </InputWrapper>
        ) : (
          <TextInput
            required={required}
            value={dateValue ? dayjs(dateValue).format("L") : ""}
            onChange={(event) => {
              const value = event.target.value
              const parsedDate = dayjs(value, "L").toDate()
              if (parsedDate && dayjs(parsedDate).isValid()) {
                handleDateChange(parsedDate)
                setIsValidDate(true)
              } else {
                setIsValidDate(false)
              }
            }}
            onBlur={() => {
              validateDate(dateValue)
            }}
            placeholder={placeholder}
            label={label}
            error={validate && !isValidDate && "Invalid date format."}
            onClick={() => setOpened((previousValue) => !previousValue)}
            rightSection={
              <CloseButton
                aria-label="Clear input"
                onClick={() => handleDateChange(undefined)}
                style={{ display: clearable && dateValue ? undefined : "none" }}
              />
            }
            {...inputProps}
          />
        )}
      </Popover.Target>

      <Popover.Dropdown maw="300px" autoFocus>
        <Stack gap="xs">
          {variant === "month" ? (
            <MonthPicker
              value={dateValue ? dayjs(dateValue).toDate() : null}
              onChange={(date) => {
                handleDateChange(date ? new Date(date) : undefined)
              }}
              minDate={minDate ? dayjs(minDate).toDate() : undefined}
              maxDate={maxDate ? dayjs(maxDate).toDate() : undefined}
            />
          ) : variant === "year" ? (
            <YearPicker
              value={dateValue}
              onChange={(date) => {
                handleDateChange(date ? new Date(date) : undefined)
                setIsValidDate(true)
              }}
            />
          ) : (
            <DatePicker
              value={dateValue ? dayjs(dateValue).toDate() : null}
              onChange={(date) => {
                handleDateChange(date ? new Date(date) : undefined)
                setIsValidDate(true)
              }}
              minDate={minDate ? dayjs(minDate).toDate() : undefined}
              maxDate={maxDate ? dayjs(maxDate).toDate() : undefined}
            />
          )}
          {dropdownChildren?.map((Child, index) => (
            <Child
              key={index}
              startOrEnd={startOrEnd}
              dateValue={dateValue ?? undefined}
              setDateValue={(newVal) => handleDateChange(newVal)}
            />
          ))}
        </Stack>
      </Popover.Dropdown>
    </Popover>
  )
}
